import { addClass, css, hasClass, removeClass } from '../utils/CssUtils';
import { catchPromiseException } from '../utils/JsUtils';
import { preventDefault } from '../utils/EventUtils';
import AbstractComponent from './AbstractComponent';
import anime from 'animejs';
import Logger from '../utils/Logger';
import { remove } from 'lodash';


export default class FilmsVerticalSlider extends AbstractComponent {
    constructor(...args) {
        Logger.log('FilmsVerticalSlider->constructor()');
        super(...args);

        this.classname = 'c-films-vertical-slider';
        this.isReady = false;
        this.isAutoplay = false;
        this.isAutoplayStopOnInteraction = false;
        this.isMute = true;
        this.isTouchDevice = navigator.userAgent.match(/(iPhone|iPod|iPad|Android|playbook|silk|BlackBerry|BB10|Windows Phone|Tizen|Bada|webOS|IEMobile|Opera Mini)/);
        this.isTouch = (('ontouchstart' in window) || (navigator.msMaxTouchPoints > 0) || (navigator.maxTouchPoints));
        this.slides = [];
        this.selectedIndex = 0;
        this.prevTime = new Date().getTime();
        this.scrollings = [];
        this.canScroll = true;
        this.g_supportsPassive = false;
        this.MSPointer = FilmsVerticalSlider.getMSPointer();
        this.events = {
            touchmove: ('ontouchmove' in window) ? 'touchmove' : this.MSPointer.move,
            touchstart: ('ontouchstart' in window) ? 'touchstart' : this.MSPointer.down
        };
        this.touchStartY = 0;
        this.touchStartX = 0;
        this.touchEndY = 0;
        this.touchEndX = 0;
        this.touchSensitivity = 5;
        this.animation = {
            slide: {
                easing: 'cubicBezier( 0.25, 0.1, 0.25, 1 )',
                duration: 600
            },
            info: {
                easing: 'cubicBezier( 0.25, 0.1, 0.25, 1 )',
                duration: 300,
                delay: '+=100'
            }
        }

        this.init();
    }

    ready() {
        return this.isReady;
    }

    init() {
        this.initSlides();
        this.initSlider();
        this.initMute();
        this.initNavigation();
        this.testPassive();
        this.addMouseWheelHandler();
        this.addKeyDownHandler();
        this.addTouchHandler();
    }

    initSlides() {
        const slidesElements = this.$el.querySelectorAll(this.getSelector('__slide'));
        for (let i = 0; i < slidesElements.length; i++) {
            const slideElement = slidesElements[i];

            const slideObject = {
                index: i,
                element: slideElement,
                video: null,
                videoDuration: null,
                button_href: null
            }

            slideObject.video = slideElement.querySelector('.c-media-fullscreen .c-media-cover__media');
            if (slideObject.video) {
                const loadedMetadatalistener = slideObject.video.addEventListener('loadedmetadata', () => {
                    slideObject.video.removeEventListener('loadedmetadata', loadedMetadatalistener);
                    slideObject.videoDuration = slideObject.video.duration;
                });
                slideObject.video.setAttribute('preload', 'metadata');
            }

            const slideButton = slideElement.querySelector('.c-button');
            if (slideButton) {
                slideObject.button_href = slideButton.href;
            }

            slideObject.navigationItem = this.$el.querySelector(this.getSelector('__navigation__item[data-index="' + i + '"]'));
            if (slideObject.navigationItem) {
                slideObject.navigationItemButton = slideObject.navigationItem.querySelector(this.getSelector('__navigation__item__button'));
                if (slideObject.navigationItemButton) {
                    slideObject.navigationItemButton.addEventListener('click', () => this.navigationItemButtonClicked(i));

                    const navigationItemLabelElement = slideObject.navigationItemButton.querySelector(this.getSelector('__navigation__item__label'));
                    if (navigationItemLabelElement) {
                        slideObject.navigationItemLabel = navigationItemLabelElement.innerHTML.trim();
                    }
                    const navigationItemCountElement = slideObject.navigationItemButton.querySelector(this.getSelector('__navigation__item__count'));
                    if (navigationItemCountElement) {
                        slideObject.navigationItemCount = navigationItemCountElement.innerHTML.trim();
                    }
                }
            }

            this.slides.push(slideObject);
        }
    }

    navigationItemButtonClicked(index) {
        if (index === this.selectedIndex) {
            if (this.slides[index].button_href) {
                window.location.href = this.slides[index].button_href;
            }
        }

        this.handleAutoplayStopOnInteraction();
        if (index > this.selectedIndex) {
            this.showSlideDown(index);
        } else {
            this.showSlideUp(index);
        }
    }

    initSlider() {
        // Setting the 'autoplay' & 'autoplay-stop-on-interaction' statuses
        this.isAutoplay = true;
        this.isAutoplayStopOnInteraction = true;

        if (this.slides.length > 0) {
            const firstSlide = this.slides[this.selectedIndex];

            css(firstSlide.element, { transform: 'translateY( 0 )' });

            if (firstSlide.video) {
                // Adding the 'canplaythrough' listener to the first slide's video
                this.backgroundVideoReadyListener = () => {
                    firstSlide.video.removeEventListener('canplaythrough', this.backgroundVideoReadyListener);
                    firstSlide.currentTime = 0;
                    this.sliderReady();
                };
                firstSlide.video.addEventListener('canplaythrough', this.backgroundVideoReadyListener);

                // Playing the first slide's video
                catchPromiseException(firstSlide.video.play());
            } else {
                this.sliderReady();
            }
        }
    }

    initMute() {
        this.$muteButton = this.$el.querySelector(this.getSelector('__mute-button'));
        if (this.$muteButton) {
            this.$muteButton.addEventListener('click', this.muteUnmute.bind(this));
        }
    }

    muteUnmute() {
        this.$muteButton.blur();
        const muteOnClass = this.getClassname('__mute-button--mute-on');
        const muteOffClass = this.getClassname('__mute-button--mute-off');
        if (hasClass(this.$muteButton, muteOnClass)) {
            removeClass(this.$muteButton, muteOnClass);
            addClass(this.$muteButton, muteOffClass);
        } else if (hasClass(this.$muteButton, muteOffClass)) {
            removeClass(this.$muteButton, muteOffClass);
            addClass(this.$muteButton, muteOnClass);
        }
        this.isMute = !this.isMute;

        const currentSlide = this.slides[this.selectedIndex];
        if (currentSlide.video) {
            currentSlide.video.muted = this.isMute;
        }
    }

    initNavigation() {
        this.$navigationDropdownPlaceholderLabel = this.$el.querySelector(this.getSelector('__navigation__dropdown__placeholder__label'));
        this.$navigationDropdownPlaceholderCount = this.$el.querySelector(this.getSelector('__navigation__dropdown__placeholder__count'));

        this.$navigationDropdownSelect = this.$el.querySelector(this.getSelector('__navigation__dropdown__select'));
        if (this.$navigationDropdownSelect) {
            this.$navigationDropdownSelect.addEventListener('change', this.navigationDropdownSelectChanged.bind(this));
        }
    }

    navigationDropdownSelectChanged(e) {
        const index = e.target.value;
        this.handleAutoplayStopOnInteraction();

        if (index > this.selectedIndex) {
            this.showSlideDown(index);
        } else {
            this.showSlideUp(index);
        }
    }

    updateNavigation(currentSlide) {
        const navigationItemCurrentClass = this.getClassname('__navigation__item--current');

        // Remove current class on all items
        for (let i = 0; i < this.slides.length; i++) {
            const slide = this.slides[i];
            if (slide.navigationItem) {
                removeClass(slide.navigationItem, navigationItemCurrentClass);
            }
        }

        // Add current class on current item
        if (currentSlide && currentSlide.navigationItem) {
            addClass(currentSlide.navigationItem, navigationItemCurrentClass);
        }

        // Update navigation dropdown placeholder
        if (this.$navigationDropdownPlaceholderLabel && currentSlide && currentSlide.navigationItemLabel) {
            this.$navigationDropdownPlaceholderLabel.innerHTML = currentSlide.navigationItemLabel;
        }
        if (this.$navigationDropdownPlaceholderCount && currentSlide && currentSlide.navigationItemCount) {
            this.$navigationDropdownPlaceholderCount.innerHTML = currentSlide.navigationItemCount;
        }
    }

    initAutoplay() {
        // Checking if autoplay is enabled
        if (this.isAutoplay) {
            // Requesting the next frame
            requestAnimationFrame(this.autoplayHandler.bind(this));
        }
    }

    autoplayHandler() {
        // Checking if autoplay is enabled
        if (this.isAutoplay) {
            // Getting the current slide
            const currentSlide = this.slides[this.selectedIndex];

            // Checking if the current slide is a video
            if (currentSlide.video && currentSlide.videoDuration) {
                // Checking the progress of the video (minus the slide animation duration)
                if (currentSlide.video.currentTime > (currentSlide.videoDuration - (this.animation.slide.duration / 1000))) {
                    // Scrolling down to the next slide
                    this.scrollDown();
                    return;
                }

                // Requesting the next frame
                requestAnimationFrame(this.autoplayHandler.bind(this));
                return;
            }
        }
    }

    handleAutoplayStopOnInteraction(autoplay = false) {
        // Checking if autoplay needs to disabled on interaction
        if (this.isAutoplayStopOnInteraction) {
            // Disabling autoplay
            this.isAutoplay = autoplay;

            // Requesting the next frame
            if (this.isAutoplay) {
                requestAnimationFrame(this.autoplayHandler.bind(this));
            }
        }
    }

    sliderReady() {
        this.isReady = true;

        this.initAutoplay();

        for (let i = 0; i < this.slides.length; i++) {
            const slide = this.slides[i];
            if (slide.video) {
                setTimeout(() => slide.video.setAttribute('preload', 'auto'), ((i - 1) * 1000));
            }
        }
    }

    testPassive() {
        var g_supportsPassive = false;
        try {
            var opts = Object.defineProperty({}, 'passive', {
                get: function () {
                    g_supportsPassive = true;
                    return g_supportsPassive;
                }
            });
            window.addEventListener('testPassive', null, opts);
            window.removeEventListener('testPassive', null, opts);
        } catch (e) { /* Silently fails */ }

        this.g_supportsPassive = g_supportsPassive;
    }

    addMouseWheelHandler() {
        var prefix = '';
        var _addEventListener;

        if (window.addEventListener) {
            _addEventListener = 'addEventListener';
        } else {
            _addEventListener = 'attachEvent';
            prefix = 'on';
        }

        // Detect available wheel event
        var support = 'onwheel' in document.createElement('div') ? 'wheel' : // Modern browsers support 'wheel'
            (document.onmousewheel !== undefined) ? 'mousewheel' : // Webkit and IE support at least 'mousewheel'
                'DOMMouseScroll'; // Let's assume that remaining browsers are older Firefox
        var passiveEvent = this.g_supportsPassive ? { passive: false } : false;

        this.boundMouseWheelHandler = this.mouseWheelHandler.bind(this);

        if (support == 'DOMMouseScroll') {
            document[_addEventListener](prefix + 'MozMousePixelScroll', this.boundMouseWheelHandler, passiveEvent);
        }
        // Handle MozMousePixelScroll in older Firefox
        else {
            document[_addEventListener](prefix + support, this.boundMouseWheelHandler, passiveEvent);
        }
    }

    mouseWheelHandler(e) {
        // Cross-browser wheel delta
        e = e || window.event;
        var value = e.wheelDelta || -e.deltaY || -e.detail;
        var delta = Math.max(-1, Math.min(1, value));

        var horizontalDetection = typeof e.wheelDeltaX !== 'undefined' || typeof e.deltaX !== 'undefined';
        var isScrollingVertically = (Math.abs(e.wheelDeltaX) < Math.abs(e.wheelDelta)) || (Math.abs(e.deltaX) < Math.abs(e.deltaY) || !horizontalDetection);

        // Limiting the array to 150 (lets not waste memory!)
        if (this.scrollings.length > 149) {
            this.scrollings.shift();
        }

        // Keeping record of the previous scrollings
        this.scrollings.push(Math.abs(value));

        // Preventing to scroll the site on mouse wheel
        preventDefault(e);

        // Time difference between the last scroll and the current one
        var curTime = new Date().getTime();
        var timeDiff = curTime - this.prevTime;
        this.prevTime = curTime;

        // Haven't they scrolled in a while?
        // (enough to be consider a different scrolling action to scroll another section)
        if (timeDiff > 750) {
            // Emptying the array, we dont care about old scrollings for our averages
            this.scrollings = [];
        }

        if (this.canScroll) {
            var averageEnd = FilmsVerticalSlider.getAverage(this.scrollings, 10);
            var averageMiddle = FilmsVerticalSlider.getAverage(this.scrollings, 70);
            var isAccelerating = averageEnd >= averageMiddle;

            // To avoid double swipes...
            if (isAccelerating && isScrollingVertically) {
                // Scrolling down?
                if (delta < 0) {
                    this.handleAutoplayStopOnInteraction();
                    this.scrollDown();
                }
                // Scrolling up?
                else {
                    this.handleAutoplayStopOnInteraction();
                    this.scrollUp();
                }
            }
        }

        return false;
    }

    scrollUp() {
        const index = (this.selectedIndex === 0) ? (this.slides.length - 1) : (this.selectedIndex - 1);
        this.showSlideUp(index);
    }

    scrollDown() {
        const index = (this.selectedIndex === (this.slides.length - 1)) ? 0 : (this.selectedIndex + 1);
        this.showSlideDown(index);
    }

    showSlideUp(index) {
        // Logger.log('Scrolling: UP');

        if (index === this.selectedIndex) { return; }
        if (!this.canScroll) { return; }

        this.canScroll = false;

        const currentSlide = this.slides[this.selectedIndex];
        const slide = this.slides[index];

        if (slide.video) {
            catchPromiseException(slide.video.play());
        }

        // Animating the slide
        anime.timeline({
            easing: this.animation.slide.easing,
            duration: this.animation.slide.duration
        })
            .add({
                targets: currentSlide.element,
                zIndex: 2,
                duration: 0
            })
            .add({
                targets: slide.element,
                zIndex: 1,
                duration: 0
            })
            .add({
                targets: slide.element,
                translateY: 0,
                duration: 1
            })
            .add({
                targets: currentSlide.element,
                translateY: [0, '100%']
            })
            .finished.then(() => {
                this.canScroll = true;
                this.selectedIndex = index;
                this.updateNavigation(slide);

                if (slide.video) {
                    slide.video.muted = this.isMute;
                }

                if (currentSlide.video) {
                    currentSlide.video.muted = true;
                    catchPromiseException(currentSlide.video.pause());
                }

                if (this.isAutoplay) {
                    requestAnimationFrame(this.autoplayHandler.bind(this));
                }
            });
    }

    showSlideDown(index) {
        // Logger.log( 'showSlideDown' );

        if (index === this.selectedIndex) { return; }
        if (!this.canScroll) { return; }

        this.canScroll = false;

        const currentSlide = this.slides[this.selectedIndex];
        const slide = this.slides[index];

        if (slide.video) {
            catchPromiseException(slide.video.play());
        }

        // Animating the slide
        anime.timeline({
            easing: this.animation.slide.easing,
            duration: this.animation.slide.duration
        })
            .add({
                targets: currentSlide.element,
                zIndex: 1,
                duration: 0
            })
            .add({
                targets: slide.element,
                zIndex: 2,
                duration: 0
            })
            .add({
                targets: slide.element,
                translateY: ['100%', 0],
            })
            .add({
                targets: currentSlide.element,
                translateY: '100%',
                duration: 1
            })
            .finished.then(() => {
                this.canScroll = true;
                this.selectedIndex = index;
                this.updateNavigation(slide);

                if (slide.video) {
                    slide.video.muted = this.isMute;
                }

                if (currentSlide.video) {
                    currentSlide.video.muted = true;
                    catchPromiseException(currentSlide.video.pause());
                }

                if (this.isAutoplay) {
                    requestAnimationFrame(this.autoplayHandler.bind(this));
                }
            });
    }

    addKeyDownHandler() {
        this.boundKeyDownHandler = this.keyDownHandler.bind(this)
        document.addEventListener('keydown', this.boundKeyDownHandler);
    }

    keyDownHandler(e) {
        if (this.canScroll) {
            // Preventing the scroll with arrow keys & spacebar & Page Up & Down keys
            if ([40, 38, 32, 33, 34].indexOf(e.keyCode) > -1) {
                preventDefault(e);
            }

            // Checking the key pressed
            switch (e.keyCode) {
                // Scroll Up
                case 38: // Up Arrow
                case 33: // Page Up
                case 37: // Left Arrow
                    this.handleAutoplayStopOnInteraction();
                    this.scrollUp();
                    break;
                // Scroll Down
                case 32: // Spacebar
                    // Shift pressed
                    if (e.shiftKey) {
                        this.handleAutoplayStopOnInteraction();
                        this.scrollUp();
                        break;
                    }
                // Falls through
                case 40: // Down Arrow
                case 34: // Page Down
                case 39: // Right Arrow
                    this.handleAutoplayStopOnInteraction();
                    this.scrollDown();
                    break;
            }
        }
    }

    addTouchHandler() {
        if (this.isTouchDevice || this.isTouch) {
            const body = document.querySelector('body');
            body.addEventListener(this.events.touchmove, this.preventBouncing, { passive: false });
            this.boundTouchStartHandler = this.touchStartHandler.bind(this);
            body.addEventListener(this.events.touchstart, this.boundTouchStartHandler);
            this.boundTouchMoveHandler = this.touchMoveHandler.bind(this);
            body.addEventListener(this.events.touchmove, this.boundTouchMoveHandler, { passive: false });
        }
    }

    preventBouncing(e) {
        if (FilmsVerticalSlider.isReallyTouch(e)) {
            // Preventing the easing on iOS devices
            preventDefault(e);
        }
    }

    touchStartHandler(e) {
        if (FilmsVerticalSlider.isReallyTouch(e)) {
            var touchEvents = this.getEventsPage(e);
            this.touchStartY = touchEvents.y;
            this.touchStartX = touchEvents.x;
        }
    }

    touchMoveHandler(e) {
        if (FilmsVerticalSlider.isReallyTouch(e)) {
            // Preventing the easing on iOS devices
            preventDefault(e);

            var touchEvents = this.getEventsPage(e);
            this.touchEndY = touchEvents.y;
            this.touchEndX = touchEvents.x;

            if (this.canScroll) {
                // If movement in the X axys is greater than in the Y and the currect section has slides...
                if (Math.abs(this.touchStartX - this.touchEndX) > (Math.abs(this.touchStartY - this.touchEndY))) {
                    // Is the movement greater than the minimum resistance to scroll?
                    if (Math.abs(this.touchStartX - this.touchEndX) > (window.innerWidth / 100 * this.touchSensitivity)) {
                        if (this.touchStartX > this.touchEndX) {
                            this.handleAutoplayStopOnInteraction();
                            this.scrollDown();
                        } else {
                            this.handleAutoplayStopOnInteraction();
                            this.scrollUp();
                        }
                    }
                }
                // Vertical scrolling
                else {
                    // Is the movement greater than the minimum resistance to scroll?
                    if (Math.abs(this.touchStartY - this.touchEndY) > (window.innerHeight / 100 * this.touchSensitivity)) {
                        if (this.touchStartY > this.touchEndY) {
                            this.handleAutoplayStopOnInteraction();
                            this.scrollDown();
                        } else if (this.touchEndY > this.touchStartY) {
                            this.handleAutoplayStopOnInteraction();
                            this.scrollUp();
                        }
                    }
                }
            }
        }
    }

    /**
    * As IE >= 10 fires both touch and mouse events when using a mouse in a touchscreen
    * this way we make sure that is really a touch event what IE is detecting.
    */
    static isReallyTouch(e) {
        // If is not IE   ||  IE is detecting `touch` or `pen`
        return (typeof e.pointerType === 'undefined') || (e.pointerType != 'mouse');
    }

    /**
    * Gets the average of the last 'number' elements of the given array.
    */
    static getAverage(elements, number) {
        var sum = 0;

        // Taking `number` elements from the end to make the average, if there are not enought, 1
        var lastElements = elements.slice(Math.max(elements.length - number, 1));

        for (var i = 0; i < lastElements.length; i++) {
            sum = sum + lastElements[i];
        }

        return Math.ceil(sum / number);
    }

    /*
    * Returns and object with Microsoft pointers (for IE<11 and for IE >= 11)
    * http://msdn.microsoft.com/en-us/library/ie/dn304886(v=vs.85).aspx
    */
    static getMSPointer() {
        var pointer;

        // IE >= 11 & rest of browsers
        if (window.PointerEvent) {
            pointer = { down: 'pointerdown', move: 'pointermove' };
        }
        // IE < 11
        else {
            pointer = { down: 'MSPointerDown', move: 'MSPointerMove' };
        }

        return pointer;
    }

    /**
    * Gets the pageX and pageY properties depending on the browser.
    * https://github.com/alvarotrigo/fullPage.js/issues/194#issuecomment-34069854
    */
    getEventsPage(e) {
        var events = [];

        events.y = ((typeof e.pageY !== 'undefined') && (e.pageY || e.pageX)) ? e.pageY : e.touches[0].pageY;
        events.x = ((typeof e.pageX !== 'undefined') && (e.pageY || e.pageX)) ? e.pageX : e.touches[0].pageX;

        // In touch devices with scrollBar:true, e.pageY is detected, but we have to deal with touch events. #1008
        if (this.isTouch && FilmsVerticalSlider.isReallyTouch(e) && (typeof e.touches !== 'undefined')) {
            events.y = e.touches[0].pageY;
            events.x = e.touches[0].pageX;
        }

        return events;
    }

    destroy() {
        this.removeMouseWheelHandler();
        this.removeKeyDownHandler();
        this.removeTouchHandler();
        this.removeSlidesListeners();
        this.removeNavigationListeners();
    }

    removeMouseWheelHandler() {
        var prefix = '';
        var _removeEventListener;

        if (window.addEventListener) {
            _removeEventListener = 'removeEventListener';
        } else {
            _removeEventListener = 'detachEvent';
            prefix = 'on';
        }

        // Detect available wheel event
        var support = 'onwheel' in document.createElement('div') ? 'wheel' : // Modern browsers support 'wheel'
            (document.onmousewheel !== undefined) ? 'mousewheel' : // Webkit and IE support at least 'mousewheel'
                'DOMMouseScroll'; // Let's assume that remaining browsers are older Firefox

        if (support == 'DOMMouseScroll') {
            document[_removeEventListener](prefix + 'MozMousePixelScroll', this.boundMouseWheelHandler);
        }
        // Handle MozMousePixelScroll in older Firefox
        else {
            document[_removeEventListener](prefix + support, this.boundMouseWheelHandler);
        }
    }

    removeKeyDownHandler() {
        document.removeEventListener('keydown', this.boundKeyDownHandler);
    }

    removeTouchHandler() {
        if (this.isTouchDevice || this.isTouch) {
            const body = document.querySelector('body');
            body.removeEventListener(this.events.touchmove, this.preventBouncing, { passive: false });
            body.removeEventListener(this.events.touchstart, this.boundTouchStartHandler);
            body.removeEventListener(this.events.touchmove, this.boundTouchMoveHandler, { passive: false });
        }
    }

    removeSlidesListeners() {
        for (let i = 0; i < this.slides.length; i++) {
            const slide = this.slides[i];
            if (slide.play && slide.playClickEventListener) {
                slide.play.removeEventListener('click', slide.playClickEventListener);
            }
        }
    }

    removeNavigationListeners() {

    }
}
